/** @format */

import {
	BVEventObject,
	BVEventObjectName,
	BVEventObjectType,
	BVEventType,
	BVProduct
} from '../models';
import {
	BVPixelResponse,
	FeedbackType,
	Helpful,
	Review,
	ReviewService,
	VoteType,
	selectBVPixel
} from '@product-reviews/data-access';
import { Component, inject, Input, OnInit } from '@angular/core';

import { LocalStorageService } from '@shared/data-access/services';
import { SkeletonShape } from '../models/skeleton-loader.interface';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import {
	HomefieldIconLibraryModule,
	homefieldIconReactionDislike,
	homefieldIconReactionLike,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { CommonModule } from '@angular/common';
import { SkeletonLoaderDirective } from '../directive/skeleton-loader/skeleton-loader.directive';

@Component({
	selector: 'product-reviews-helpful',
	templateUrl: './review-helpful.component.html',
	styleUrl: './review-helpful.component.scss',
	imports: [CommonModule, HomefieldIconLibraryModule, SkeletonLoaderDirective]
})
export class ReviewHelpfulComponent implements OnInit {
	/**
	 *
	 * @description
	 * Review - Receive the review to show helpful data from review
	 */
	@Input() review: Review;
	@Input() isLoading: boolean;

	private readonly reviewService = inject(ReviewService);
	private readonly localStorageService = inject(LocalStorageService);
	private readonly store = inject(Store<BVPixelResponse>);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	SkeletonShape = SkeletonShape;
	dataStorage: Helpful[] = [];
	flagStorage = false;
	voteType = VoteType;
	labelReport = 'Report';
	stateButtons = {
		vote: false,
		report: false
	};
	iconColors = {
		like: 'var(--hmf-gray700)',
		dislike: 'var(--hmf-gray700)'
	};
	totalCounts = {
		positive: 0,
		negative: 0
	};

	selectBV$ = this.store.select(selectBVPixel);
	bvPixelResponse: BVPixelResponse;

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconReactionLike,
			homefieldIconReactionDislike
		]);
	}

	ngOnInit(): void {
		this.selectBV$.pipe(take(1)).subscribe((bv: BVPixelResponse) => {
			this.bvPixelResponse = bv;
		});

		this.totalCounts = {
			positive: this.review?.TotalPositiveFeedbackCount ?? 0,
			negative: this.review?.TotalNegativeFeedbackCount ?? 0
		};

		this.getHelpfulFromStore();
		if (this.dataStorage.length !== 0) {
			this.initialStorageData();
		}
	}

	/**
	 * @description Handles the click event on a voting button to record a vote.
	 *
	 * @param {VoteType} vote - The type of vote, which can be positive or negative.
	 */
	voteButtonClicked(vote: VoteType): void {
		switch (vote) {
			case VoteType.POSITIVE:
				this.totalCounts.positive += 1;
				break;
			case VoteType.NEGATIVE:
				this.totalCounts.negative += 1;
				break;
		}
		if (vote !== VoteType.NONE) {
			this.setIconColors(vote);
		}
		this.stateButtons.vote = true;
		if (!this.flagStorage) {
			this.submitFeedback(vote, FeedbackType.HELPFULNESS);
			this.bvPixelEvent(BVEventObjectName.HELPFULNESS, vote);
		}
	}

	/**
	 * @description Handles the click event of the report button.
	 */
	reportButtonClicked() {
		this.labelReport = 'Reported';
		this.stateButtons.report = true;
		if (!this.flagStorage) {
			this.submitFeedback(VoteType.NONE, FeedbackType.INAPPROPRIATE);
			this.bvPixelEvent(BVEventObjectName.REPORT);
		}
	}

	initialStorageData() {
		if (this.review) {
			const helpfulLocalStorage = this.findHelpfulById(this.dataStorage, this.review.Id);
			if (helpfulLocalStorage) {
				this.flagStorage = true;
				this.voteButtonClicked(helpfulLocalStorage.vote);
				if (helpfulLocalStorage.feedbackType === FeedbackType.INAPPROPRIATE) {
					this.reportButtonClicked();
				}
			}
		}
	}

	getHelpfulFromStore() {
		let helpfulArray: Helpful[] = [];
		const helpfulStorage = this.localStorageService.get('TAG_HELPFUL_KEY');
		if (helpfulStorage) {
			helpfulArray = JSON.parse(helpfulStorage);
		}
		this.dataStorage = helpfulArray;
	}

	/**
	 * @description Submits a vote and feedback type to the review service.
	 *
	 * @param {VoteType} vote - The vote type (e.g., positive or negative).
	 * @param {FeedbackType} typeFeedback - The feedback type (e.g., helpfulness or inappropriate).
	 */
	submitFeedback(vote: VoteType, feedbackType: FeedbackType) {
		const helpFulBody: Helpful = {
			vote,
			feedbackType,
			contentId: this.review.Id
		};

		this.getHelpfulFromStore();
		if (this.dataStorage.length === 0) {
			this.dataStorage.push(helpFulBody);
		}

		const helpfulArray = this.findAndReplaceById(this.dataStorage, helpFulBody);
		this.localStorageService.set('TAG_HELPFUL_KEY', JSON.stringify(helpfulArray));

		this.reviewService
			.submitFeedback$(helpFulBody)
			.pipe(take(1))
			.subscribe((response) => {
				if (response.Feedback) {
					const destructuringReview = { ...this.review };
					switch (feedbackType) {
						case FeedbackType.HELPFULNESS:
							destructuringReview.Helpfulness += 1;
							break;
						case FeedbackType.INAPPROPRIATE:
							destructuringReview.TotalInappropriateFeedbackCount += 1;
							break;
					}
					this.review = destructuringReview;
				}
			});
	}

	findAndReplaceById(helpfulArray: Helpful[], newData: Helpful): Helpful[] {
		const index = helpfulArray.findIndex((helpful) => helpful.contentId === newData.contentId);
		const helpfulCopied = [...helpfulArray];
		if (index === -1) {
			helpfulCopied.push(newData);
			return helpfulCopied;
		}
		helpfulCopied[index].vote =
			helpfulCopied[index].vote !== VoteType.NONE ? helpfulCopied[index].vote : newData.vote;
		helpfulCopied[index].feedbackType =
			helpfulCopied[index].feedbackType === FeedbackType.INAPPROPRIATE
				? helpfulCopied[index].feedbackType
				: newData.feedbackType;
		return helpfulCopied;
	}

	private findHelpfulById(helpfulArray: Helpful[], id: string): Helpful | null {
		const foundHelpful = helpfulArray.find((helpful) => helpful.contentId === id);
		return foundHelpful || null;
	}

	protected readonly skeletonShape = SkeletonShape;
	bvPixelEvent(name: string, detail1?: string, detail2?: string): void {
		const bvObject: BVEventObject = {
			type: BVEventObjectType.USED,
			name: name,
			brand: this.bvPixelResponse.brand,
			productId: this.bvPixelResponse.productId,
			bvProduct: BVProduct.RATINGSANDREVIEWS,
			categoryId: this.bvPixelResponse.category,
			detail1: detail1 || '',
			detail2: detail2 || ''
		};
		BV.pixel.trackEvent(BVEventType.FEATURE, bvObject);
	}

	setIconColors(vote: VoteType) {
		const colorMapping = {
			[VoteType.POSITIVE]: { like: '', dislike: 'var(--hmf-gray700)' },
			[VoteType.NEGATIVE]: { like: 'var(--hmf-gray700)', dislike: '' }
		};

		this.iconColors = colorMapping[vote];
	}
}
